import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Star, 
  MapPin, 
  Calendar, 
  Briefcase, 
  Users, 
  CheckCircle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Globe,
  Building,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Share2,
  AlertTriangle,
  Lightbulb,
  ChevronRight,
  Image as ImageIcon,
  Award,
  GraduationCap,
  Flame,
  Play,
  Video,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper component to render text with preserved formatting (like Word/Wikipedia)
const FormattedText = ({ text, className = '' }) => {
  if (!text) return null;
  
  // Split by double newlines for paragraphs, preserve single newlines within
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  
  return (
    <div className={`space-y-4 ${className}`}>
      {paragraphs.map((paragraph, idx) => (
        <p key={idx} className="text-gray-700 leading-relaxed whitespace-pre-line">
          {paragraph.trim()}
        </p>
      ))}
    </div>
  );
};

// Rating Summary Popup Component
const RatingSummaryPopup = ({ isOpen, onClose, leader }) => {
  if (!isOpen) return null;

  const avgRating = leader?.avg_rating || 0;
  const totalRatings = leader?.rating_count || 0;
  const devRating = leader?.development_rating || 0;
  const honRating = leader?.honesty_rating || 0;
  const resRating = leader?.responsiveness_rating || 0;

  // Calculate rating breakdown (simulated distribution based on average)
  const getRatingBreakdown = () => {
    if (totalRatings === 0) return [0, 0, 0, 0, 0];
    // Simulate distribution - in production this would come from backend
    const distribution = [
      Math.round(totalRatings * 0.05), // 1 star
      Math.round(totalRatings * 0.05), // 2 star
      Math.round(totalRatings * 0.10), // 3 star
      Math.round(totalRatings * 0.20), // 4 star
      Math.round(totalRatings * 0.60), // 5 star
    ];
    // Adjust to match total
    const sum = distribution.reduce((a, b) => a + b, 0);
    if (sum !== totalRatings) {
      distribution[4] += totalRatings - sum;
    }
    return distribution.reverse(); // 5 star first
  };

  const breakdown = getRatingBreakdown();
  const maxBreakdown = Math.max(...breakdown, 1);

  const renderStars = (count, size = 'h-4 w-4') => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= Math.round(count) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Rating Summary</h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-rating-popup"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Overall Rating */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              {renderStars(avgRating, 'h-6 w-6')}
            </div>
            <p className="text-3xl font-bold text-gray-900">{avgRating.toFixed(1)} / 5</p>
            <p className="text-sm text-gray-500 mt-1">{totalRatings} {totalRatings === 1 ? 'Rating' : 'Ratings'}</p>
          </div>

          {/* Rating Breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Rating Breakdown</h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((stars, idx) => (
                <div key={stars} className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 w-12">{stars} star</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                      style={{ width: `${(breakdown[idx] / maxBreakdown) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-8 text-right">{breakdown[idx]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Category-wise Ratings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Category-wise Ratings</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Development</span>
                <div className="flex items-center gap-2">
                  {devRating > 0 ? (
                    <>
                      {renderStars(devRating, 'h-3.5 w-3.5')}
                      <span className="text-sm font-medium text-gray-700">{devRating.toFixed(1)}</span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">No ratings yet</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Honesty</span>
                <div className="flex items-center gap-2">
                  {honRating > 0 ? (
                    <>
                      {renderStars(honRating, 'h-3.5 w-3.5')}
                      <span className="text-sm font-medium text-gray-700">{honRating.toFixed(1)}</span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">No ratings yet</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Responsiveness</span>
                <div className="flex items-center gap-2">
                  {resRating > 0 ? (
                    <>
                      {renderStars(resRating, 'h-3.5 w-3.5')}
                      <span className="text-sm font-medium text-gray-700">{resRating.toFixed(1)}</span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">No ratings yet</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <Button
            onClick={onClose}
            className="w-full bg-purple-600 text-white hover:bg-purple-700"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const LeaderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canParticipate, triggerOnboarding } = useAuth();
  const { toast } = useToast();
  const [leader, setLeader] = useState(null);
  const [ratings, setRatings] = useState({ development: 0, honesty: 0, responsiveness: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bioExpanded, setBioExpanded] = useState(false);
  const [showRatingSummary, setShowRatingSummary] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    fetchLeader();
  }, [id]);

  const fetchLeader = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaders/${id}`);
      setLeader(response.data);
    } catch (error) {
      console.error('Error fetching leader:', error);
      try {
        const fallbackResponse = await axios.get(`${API_URL}/api/politicians/${id}`);
        setLeader({
          ...fallbackResponse.data,
          leader_id: fallbackResponse.data.politician_id,
          photo_url: fallbackResponse.data.profile_image,
          role: 'MLA',
          status: 'active'
        });
      } catch (fallbackError) {
        console.error('Error fetching politician fallback:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRate = async () => {
    // Check if user is logged in
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'Please login to submit your rating.',
        variant: 'destructive',
      });
      return;
    }

    if (!ratings.development || !ratings.honesty || !ratings.responsiveness) {
      toast({
        title: 'Incomplete Rating',
        description: 'Please rate all categories before submitting.',
        variant: 'destructive',
      });
      return;
    }

    setSubmittingRating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/leaders/${id}/rate`,
        ratings,
        { withCredentials: true }
      );
      
      // Check if it was an update or new rating
      const isUpdate = response.data?.message?.toLowerCase().includes('updated');
      
      toast({
        title: 'Success',
        description: isUpdate 
          ? '✅ Your rating has been updated successfully.'
          : '✅ Your rating has been submitted successfully.',
      });
      
      // Refresh leader data to get updated ratings
      await fetchLeader();
      setRatings({ development: 0, honesty: 0, responsiveness: 0 });
    } catch (error) {
      toast({
        title: 'Error',
        description: '❌ Unable to submit your rating. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleReportIssue = () => {
    if (user && !canParticipate()) {
      triggerOnboarding();
      return;
    }
    navigate('/issues/new', {
      state: {
        prefilledState: leader.state,
        prefilledConstituency: leader.constituency
      }
    });
  };

  const renderStars = (category, value) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            data-testid={`${category}-star-${star}`}
            onClick={() => setRatings({ ...ratings, [category]: star })}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`h-6 w-6 md:h-6 md:w-6 ${
                star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Tabs configuration - Only Overview, Achievements, Controversies, Media
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'achievements', label: 'Achievements', icon: Award },
    { id: 'controversies', label: 'Controversies', icon: Flame },
    { id: 'media', label: 'Media', icon: ImageIcon },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!leader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Leader not found</p>
      </div>
    );
  }

  // Helper function to clean biography text (remove source labels like "Wikipedia")
  const cleanBioText = (text) => {
    if (!text) return '';
    // Remove standalone "Wikipedia" lines and similar source labels
    return text
      .replace(/^Wikipedia\s*$/gim, '')
      .replace(/\n\s*Wikipedia\s*\n/gi, '\n\n')
      .replace(/\s*Wikipedia\s*$/gi, '')
      .replace(/^Source:.*$/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Smart truncation that doesn't cut words and respects paragraph boundaries
  const getTruncatedBio = (text, maxChars = 350) => {
    if (!text || text.length <= maxChars) {
      return { preview: text, hasMore: false };
    }
    
    // Find a good break point - prefer paragraph break, then sentence, then word
    let breakPoint = maxChars;
    
    // Look for paragraph break within range
    const paragraphBreak = text.lastIndexOf('\n\n', maxChars);
    if (paragraphBreak > maxChars * 0.5) {
      breakPoint = paragraphBreak;
    } else {
      // Look for sentence break (. ! ?) within range
      const sentenceMatch = text.substring(0, maxChars).match(/[.!?]\s+[A-Z]/g);
      if (sentenceMatch) {
        const lastSentence = text.lastIndexOf(sentenceMatch[sentenceMatch.length - 1]?.charAt(0) || '.', maxChars);
        if (lastSentence > maxChars * 0.5) {
          breakPoint = lastSentence + 1;
        }
      }
      
      // If no good sentence break, find last space to avoid cutting words
      if (breakPoint === maxChars) {
        const lastSpace = text.lastIndexOf(' ', maxChars);
        if (lastSpace > maxChars * 0.6) {
          breakPoint = lastSpace;
        }
      }
    }
    
    return {
      preview: text.substring(0, breakPoint).trim(),
      hasMore: true
    };
  };

  // Process biography
  const rawBioText = leader.bio || '';
  const cleanedBio = cleanBioText(rawBioText);
  const { preview: bioPreview, hasMore: hasMoreBio } = getTruncatedBio(cleanedBio, 350);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Rating Summary Popup */}
      <RatingSummaryPopup 
        isOpen={showRatingSummary} 
        onClose={() => setShowRatingSummary(false)} 
        leader={leader}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-6">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
          {/* Main Content Area - 70% on desktop */}
          <div className="flex-1 lg:w-[70%]">
            {/* Profile Header Card - Compact on mobile */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Profile Image - Smaller on mobile */}
                <div className="flex-shrink-0 flex items-start justify-center md:justify-start gap-3 md:block">
                  <img
                    src={leader.photo_url || leader.profile_image || '/logo.png'}
                    alt={leader.name}
                    className="w-20 h-20 md:w-32 md:h-32 rounded-full object-cover border-3 md:border-4 border-gray-100 shadow-md"
                    onError={(e) => { e.target.src = '/logo.png'; }}
                  />
                  {/* Rating beside image on mobile */}
                  <div className="flex flex-col items-center md:hidden">
                    <button
                      onClick={() => setShowRatingSummary(true)}
                      className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                      data-testid="rating-display-mobile"
                    >
                      <span className="text-2xl font-bold text-gray-900">{(leader.avg_rating || 0).toFixed(1)}</span>
                      <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                    </button>
                    <p className="text-xs text-gray-500">{leader.rating_count || 0} ratings</p>
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center md:text-left">
                  {/* Name and Status */}
                  <div className="flex flex-wrap items-center gap-2 justify-center md:justify-start mb-1.5 md:mb-2">
                    <h1 className="text-xl md:text-3xl font-bold text-gray-900">{leader.name}</h1>
                    <span className="inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-medium bg-green-50 text-green-600 rounded-full border border-green-200">
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4" />
                      Active
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-1.5 md:gap-2 justify-center md:justify-start mb-2 md:mb-3">
                    <span className="px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-semibold bg-purple-100 text-purple-700 rounded-md">
                      {leader.role || 'MLA'}
                    </span>
                    <span className="px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-semibold bg-orange-100 text-orange-600 rounded-md border border-orange-200">
                      {leader.party || 'BJP'}
                    </span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 md:gap-2 text-gray-500 justify-center md:justify-start">
                    <MapPin className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    <span className="text-xs md:text-sm">{leader.constituency}, {leader.state || 'Assam'}</span>
                  </div>
                </div>

                {/* Rating - Desktop only (hidden on mobile, shown beside image) */}
                <div className="flex-shrink-0 text-center hidden md:block">
                  <button
                    onClick={() => setShowRatingSummary(true)}
                    className="flex items-center justify-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                    data-testid="rating-display-desktop"
                  >
                    <span className="text-4xl font-bold text-gray-900">{(leader.avg_rating || 0).toFixed(1)}</span>
                    <Star className="h-8 w-8 fill-yellow-400 text-yellow-400" />
                  </button>
                  <p className="text-sm text-gray-500">{leader.rating_count || 0} ratings</p>
                </div>
              </div>

              {/* Info Strip - 4 Cards - Compact on mobile */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100">
                {/* Born */}
                <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 rounded-lg">
                  <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <Calendar className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Born</p>
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate leading-tight">{leader.birth_date || 'N/A'}</p>
                  </div>
                </div>

                {/* Current Position */}
                <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 rounded-lg">
                  <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <Briefcase className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Position</p>
                    <p className="text-xs md:text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{leader.position || leader.role || 'MLA'}</p>
                  </div>
                </div>

                {/* Assumed Office */}
                <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 rounded-lg">
                  <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <Award className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-gray-500">In Office</p>
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate leading-tight">{leader.assumed_office || 'N/A'}</p>
                  </div>
                </div>

                {/* Party */}
                <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 rounded-lg">
                  <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <Users className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-gray-500">Party</p>
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate leading-tight">{leader.party || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs - Compact on mobile */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="border-b border-gray-100 overflow-x-auto scrollbar-hide">
                <div className="flex">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2.5 md:py-3.5 text-[13px] md:text-sm font-medium whitespace-nowrap transition-colors ${
                          activeTab === tab.id
                            ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-4 md:p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6 md:space-y-8">
                    {/* Biography Section - Wikipedia Style */}
                    <section>
                      <div className="flex items-center gap-2 mb-3 md:mb-4">
                        <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                        <h2 className="text-base md:text-lg font-semibold text-purple-600">Biography</h2>
                      </div>
                      
                      {/* Biography with Read More/Less */}
                      <article className="prose prose-gray max-w-none">
                        {cleanedBio ? (
                          <div className="relative">
                            {/* Biography Content */}
                            <div 
                              className={`text-gray-700 leading-relaxed md:leading-7 text-sm md:text-base whitespace-pre-line transition-all duration-300 ${
                                !bioExpanded && hasMoreBio ? 'line-clamp-none' : ''
                              }`}
                            >
                              {bioExpanded ? cleanedBio : bioPreview}
                            </div>
                            
                            {/* Fade overlay when collapsed */}
                            {hasMoreBio && !bioExpanded && (
                              <div className="absolute bottom-0 left-0 right-0 h-12 md:h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic text-sm">Biography information not available.</p>
                        )}
                        
                        {/* Read More / Read Less Button */}
                        {hasMoreBio && (
                          <div className={`flex justify-center ${bioExpanded ? 'mt-4 md:mt-6' : 'mt-1 md:mt-2 relative z-10'}`}>
                            <button
                              onClick={() => setBioExpanded(!bioExpanded)}
                              className="inline-flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 transition-colors shadow-sm"
                            >
                              {bioExpanded ? (
                                <>
                                  Read Less
                                  <ChevronUp className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                </>
                              ) : (
                                <>
                                  Read More
                                  <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </article>
                    </section>

                    {/* Early Life & Education Section */}
                    <section>
                      <div className="flex items-center gap-2 mb-3 md:mb-4">
                        <GraduationCap className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                        <h2 className="text-base md:text-lg font-semibold text-purple-600">Early Life & Education</h2>
                      </div>

                      {leader.early_life_education ? (
                        <FormattedText text={leader.early_life_education} />
                      ) : (
                        <div className="space-y-2 md:space-y-3">
                          <div className="flex items-start gap-2 md:gap-3">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                              <span className="font-semibold text-gray-900">Birth Year:</span> {leader.birth_date || 'Not available'}
                            </p>
                          </div>
                          <div className="flex items-start gap-2 md:gap-3">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                              <span className="font-semibold text-gray-900">Roots:</span> {leader.birthplace || `Hails from ${leader.constituency}, ${leader.state || 'Assam'}`}
                            </p>
                          </div>
                          <div className="flex items-start gap-2 md:gap-3">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                              <span className="font-semibold text-gray-900">Education:</span> {leader.education || 'Education details not available'}
                            </p>
                          </div>
                          <div className="flex items-start gap-2 md:gap-3">
                            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                              <span className="font-semibold text-gray-900">Family:</span> {leader.family || 'Family details not available'}
                            </p>
                          </div>
                        </div>
                      )}
                    </section>

                    {/* Rating Section - Available to all, submission requires login */}
                    <section className="border-t border-gray-100 pt-6 md:pt-8">
                      <div className="flex items-center gap-2 mb-3 md:mb-4">
                        <Star className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                        <h2 className="text-base md:text-lg font-semibold text-purple-600">Rate This Leader</h2>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
                        <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-3">Development</label>
                          {renderStars('development', ratings.development)}
                        </div>
                        <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-3">Honesty</label>
                          {renderStars('honesty', ratings.honesty)}
                        </div>
                        <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-3">Responsiveness</label>
                          {renderStars('responsiveness', ratings.responsiveness)}
                        </div>
                      </div>
                      
                      <Button
                        data-testid="submit-rating-btn"
                        onClick={handleRate}
                        disabled={submittingRating}
                        className="bg-purple-600 text-white hover:bg-purple-700 px-4 md:px-6 text-sm md:text-base"
                      >
                        {submittingRating ? 'Submitting...' : 'Submit Rating'}
                      </Button>
                      {!user && (
                        <p className="text-xs text-gray-500 mt-2">Please login to submit your rating.</p>
                      )}
                    </section>
                  </div>
                )}

                {/* Achievements Tab */}
                {activeTab === 'achievements' && (
                  <div>
                    <div className="flex items-center gap-2 mb-4 md:mb-6">
                      <Award className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                      <h2 className="text-base md:text-lg font-semibold text-purple-600">Achievements</h2>
                    </div>

                    {leader.achievements ? (
                      <FormattedText text={leader.achievements} className="prose prose-gray max-w-none text-sm md:text-base" />
                    ) : (
                      <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg">
                        <Award className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 text-gray-300" />
                        <p className="text-gray-500 text-sm md:text-base">No achievements information available yet.</p>
                        <p className="text-xs md:text-sm text-gray-400 mt-2">Achievements will be added by the administrator.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Controversies Tab */}
                {activeTab === 'controversies' && (
                  <div>
                    <div className="flex items-center gap-2 mb-4 md:mb-6">
                      <Flame className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                      <h2 className="text-base md:text-lg font-semibold text-purple-600">Controversies</h2>
                    </div>

                    {leader.controversies ? (
                      <FormattedText text={leader.controversies} className="prose prose-gray max-w-none text-sm md:text-base" />
                    ) : (
                      <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg">
                        <Flame className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 text-gray-300" />
                        <p className="text-gray-500 text-sm md:text-base">No controversies information available.</p>
                        <p className="text-xs md:text-sm text-gray-400 mt-2">This section will be updated as information becomes available.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Media Tab */}
                {activeTab === 'media' && (
                  <div>
                    <div className="flex items-center gap-2 mb-4 md:mb-6">
                      <ImageIcon className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                      <h2 className="text-base md:text-lg font-semibold text-purple-600">Media</h2>
                    </div>

                    <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg">
                      <div className="flex justify-center gap-3 md:gap-4 mb-3 md:mb-4">
                        <ImageIcon className="h-8 w-8 md:h-10 md:w-10 text-gray-300" />
                        <Video className="h-8 w-8 md:h-10 md:w-10 text-gray-300" />
                        <Play className="h-8 w-8 md:h-10 md:w-10 text-gray-300" />
                      </div>
                      <p className="text-gray-500 font-medium text-sm md:text-base">Media Gallery Coming Soon</p>
                      <p className="text-xs md:text-sm text-gray-400 mt-2">Images, videos, and documents will be available here.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar - 30% on desktop - Compact on mobile */}
          <div className="w-full lg:w-[30%] lg:max-w-xs space-y-3 md:space-y-6">
            {/* Social Media Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Share2 className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Connect on Social Media</h3>
              </div>

              <div className="space-y-2 md:space-y-3">
                {/* Facebook */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Facebook className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Facebook</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.facebook || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* Instagram */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Instagram className="h-3.5 w-3.5 md:h-4 md:w-4 text-pink-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Instagram</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.instagram || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* X (Twitter) */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <Twitter className="h-3.5 w-3.5 md:h-4 md:w-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">X (Twitter)</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.twitter || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* YouTube */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Youtube className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">YouTube</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.youtube || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Phone className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Contact Information</h3>
              </div>

              <div className="space-y-2 md:space-y-3">
                {/* Phone */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.phone || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* Email */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Email</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.email || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* Official Website */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Globe className="h-3.5 w-3.5 md:h-4 md:w-4 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Website</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.website || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>

                {/* Office Address */}
                <div className="flex items-center justify-between py-1.5 md:py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building className="h-3.5 w-3.5 md:h-4 md:w-4 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-900">Office</p>
                      <p className="text-[10px] md:text-xs text-gray-400 truncate">{leader.office_address || 'Not added'}</p>
                    </div>
                  </div>
                  <button className="px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50 flex-shrink-0">
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Constituency Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <MapPin className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Constituency</h3>
              </div>

              <div className="mb-3 md:mb-4">
                <p className="font-medium text-gray-900 text-sm md:text-base">{leader.constituency} Assembly Constituency</p>
                <p className="text-xs md:text-sm text-gray-500">District: {leader.district || leader.constituency}, {leader.state || 'Assam'}</p>
              </div>

              <button 
                onClick={() => navigate(`/constituencies/${leader.constituency_id || leader.constituency}`)}
                className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-medium text-purple-600 border border-purple-200 px-3 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-purple-50 transition-colors w-full justify-center"
              >
                View Constituency Details
                <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
              </button>
            </div>

            {/* Actions Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Users className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900 text-sm md:text-base">Actions</h3>
              </div>

              <div className="space-y-1.5 md:space-y-2">
                <button
                  onClick={handleReportIssue}
                  className="w-full flex items-center justify-between p-2 md:p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  data-testid="report-issue-btn"
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-500" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-gray-700">Report Issue</span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
                </button>

                <button
                  onClick={() => navigate('/suggestions/new', { state: { leaderId: id } })}
                  className="w-full flex items-center justify-between p-2 md:p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-500" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-gray-700">Suggest Initiative</span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderDetail;
